import { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ApiService from 'utils/services/ApiService';
import { getSyncCache, getTestResults, saveTestResults } from 'utils/services/idbService';
import { setAllSitesData } from 'utils/redux/reducers/allSitesLoadedReducer';
import { updateSiteInGroup } from 'utils/redux/reducers/groupsReducer';
import { IReduxState } from 'utils/models/redux-states';
import { IGroups, ISites } from 'utils/models/groups';
import { usePopupMessage } from './SendPopUpMessage';

// Hibernate's default string column length. The backend maps site names
// without an explicit length, so longer names would be rejected.
export const SITE_NAME_MAX_LENGTH = 255;

export const hasPendingSiteSync = async (siteId: number): Promise<boolean> => {
	const { siteSyncs } = await getSyncCache();
	return (siteSyncs ?? []).some((entry: { id: number }) => entry.id === siteId);
};

//Renames a site. See components/admin-settings/Sites.tsx
//(API, Redux and the IndexedDB cache)
const useRenameSite = (siteId: number) => {
	const dispatch = useDispatch();
	const sendPopUpMessage = usePopupMessage();
	const groups: IGroups[] = useSelector((state: IReduxState) => state?.groups?.groups) ?? [];
	const [isSaving, setIsSaving] = useState(false);
	// State updates are async, so a second Enter press can arrive before
	// isSaving is true. The ref blocks it immediately.
	const savingRef = useRef(false);

	const renameSite = async (newName: string): Promise<boolean> => {
		const name = newName.trim();
		if (!name || name.length > SITE_NAME_MAX_LENGTH || savingRef.current) return false;

		savingRef.current = true;
		setIsSaving(true);
		try {
			if (await hasPendingSiteSync(siteId)) {
				sendPopUpMessage('Error', 'This site has changes waiting to sync. Try again once syncing finishes.');
				return false;
			}

			// Load the full site so the PUT only changes the name. Stop if it can't be
			// loaded rather than saving a partial site.
			const siteResult = await ApiService.getASite(siteId);
			if (siteResult.status !== 200 || siteResult.data?.id !== siteId)
				throw new Error('Could not load the site before renaming.');

			const result = await ApiService.editASite({ ...siteResult.data, name }, siteId);
			if (result.status !== 200)
				throw new Error(`Server returned ${result.status}`);

			groups.forEach((group) => {
				const site = group.sites?.find((s: ISites) => s.id === siteId);
				if (site) {
					dispatch(updateSiteInGroup({ groupId: group.id, siteId, updates: { ...site, name } }));
				}
			});

			const username = localStorage.getItem('currentUser') ?? 'unknown';
			const cachedSites = await getTestResults(`allSites`, username);
			const cachedSite = cachedSites?.find((s: { id: number }) => s.id === siteId);
			if (cachedSite) {
				cachedSite.name = name;
				await saveTestResults(`allSites`, cachedSites, username);
				dispatch(setAllSitesData(cachedSites));
			}

			sendPopUpMessage('Success', `Site renamed to ${name}.`);
			return true;
		} catch (error) {
			console.error('Rename site error:', error);
			sendPopUpMessage('Error', 'Site name not saved. Refresh the page and try again.');
			return false;
		} finally {
			savingRef.current = false;
			setIsSaving(false);
		}
	};

	return { renameSite, isSaving };
};

export default useRenameSite;
